import { useState, useMemo } from "react";
import { useParams, useSearch, useNavigate, createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { GripVertical, Pencil, Save, X, Trash2, Plus, Printer } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useWords, useUpdateWord, useDeletePage, useCategory } from "@/lib/api/vocabulary";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { toast } from "sonner";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import { PrintDialog } from "./_components/-print-dialog";

interface EditableWord {
  _id: string;
  word: string;
  meaning: string;
  page: number;
  number: number;
  category: string;
}

// Sortable Row 컴포넌트
function SortableWordRow({
  word,
  onWordChange,
}: {
  word: EditableWord;
  onWordChange: (id: string, field: "word" | "meaning", value: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: word._id,
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <div className="flex items-center gap-2">
          <button
            className="cursor-grab active:cursor-grabbing touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          {word.number}
        </div>
      </TableCell>
      <TableCell>
        <Input
          value={word.word}
          onChange={(e) => onWordChange(word._id, "word", e.target.value)}
          autoComplete="off"
          placeholder="영단어"
        />
      </TableCell>
      <TableCell>
        <Input
          value={word.meaning}
          onChange={(e) => onWordChange(word._id, "meaning", e.target.value)}
          autoComplete="off"
          placeholder="뜻"
        />
      </TableCell>
    </TableRow>
  );
}

function WordsPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as { page?: string };
  const categoryId = params.id as string;

  const currentPage = Number(searchParams.page ?? 0) || 1;
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedWords, setEditedWords] = useState<EditableWord[]>([]);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);

  const { data: category, isLoading: isCategoryLoading } = useCategory(categoryId);
  const { data: words, isLoading: isWordsLoading } = useWords(categoryId, currentPage);
  const { mutateAsync: updateWord, isPending: isUpdating } = useUpdateWord();
  const { mutateAsync: deletePage, isPending: isDeleting } = useDeletePage();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const totalPages = category?.last_page ?? 0;
  const isLoading = isCategoryLoading || isWordsLoading;

  // 드래그 앤 드롭 센서
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 현재 페이지 단어들
  const currentPageWords = useMemo(() => {
    if (isEditMode) {
      return editedWords;
    }
    return words ?? [];
  }, [words, isEditMode, editedWords]);

  // 페이지 변경
  const handlePageChange = (page: number) => {
    if (isEditMode) {
      toast.error("수정 모드를 종료한 후 페이지를 변경할 수 있습니다.");
      return;
    }
    navigate({ to: `/vocabulary/categories/${categoryId}/words?page=${page}` });
  };

  // 수정 모드 진입
  const handleEnterEditMode = () => {
    if (!words) return;
    setEditedWords(
      [...words].sort((a, b) => a.number - b.number).map((word) => ({ ...word }))
    );
    setIsEditMode(true);
  };

  // 수정 모드 취소
  const handleCancelEdit = () => {
    setEditedWords([]);
    setIsEditMode(false);
  };

  // 단어 필드 변경
  const handleWordChange = (
    id: string,
    field: "word" | "meaning",
    value: string
  ) => {
    setEditedWords((prev) =>
      prev.map((w) => (w._id === id ? { ...w, [field]: value } : w))
    );
  };

  // 드래그 앤 드롭 종료
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setEditedWords((items) => {
      const oldIndex = items.findIndex((item) => item._id === active.id);
      const newIndex = items.findIndex((item) => item._id === over.id);

      const reordered = arrayMove(items, oldIndex, newIndex);
      // 번호 재조정
      return reordered.map((word, index) => ({
        ...word,
        number: index + 1,
      }));
    });
  };

  // 저장
  const handleSave = async () => {
    try {
      // 모든 단어 업데이트
      await Promise.all(
        editedWords.map((word) =>
          updateWord({
            id: word._id,
            data: {
              word: word.word,
              meaning: word.meaning,
              page: word.page,
              number: word.number,
            },
          })
        )
      );
      toast.success("저장되었습니다.");
      setIsEditMode(false);
      setEditedWords([]);
    } catch (error) {
      toast.error("저장에 실패했습니다.");
      console.error(error);
    }
  };

  // 페이지 삭제
  const handleDeletePage = () => {
    setAlertDialogState({
      title: "페이지 삭제",
      description: `Page ${currentPage}를 삭제하시겠습니까?\n해당 페이지의 모든 단어가 삭제되고, 이후 페이지들의 번호가 재정렬됩니다.`,
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deletePage({ categoryId, page: currentPage });

          // 수정 모드 종료
          setIsEditMode(false);
          setEditedWords([]);

          // 마지막 페이지를 삭제한 경우 이전 페이지로 이동
          if (currentPage === totalPages && currentPage > 1) {
            navigate({ to: `/vocabulary/categories/${categoryId}/words?page=${currentPage - 1}` });
          } else if (totalPages === 1) {
            // 유일한 페이지를 삭제한 경우 1페이지로 리셋
            navigate({ to: `/vocabulary/categories/${categoryId}/words?page=1` });
          }

          toast.success("페이지가 삭제되었습니다.");
        } catch (error) {
          toast.error("페이지 삭제에 실패했습니다.");
          console.error(error);
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  // 페이지네이션 아이템 렌더링
  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      // 모든 페이지 표시
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={() => handlePageChange(i)}
              isActive={currentPage === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      // 첫 페이지
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            onClick={() => handlePageChange(1)}
            isActive={currentPage === 1}
          >
            1
          </PaginationLink>
        </PaginationItem>
      );

      if (currentPage > 3) {
        items.push(<PaginationEllipsis key="ellipsis-1" />);
      }

      // 현재 페이지 주변
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={() => handlePageChange(i)}
              isActive={currentPage === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (currentPage < totalPages - 2) {
        items.push(<PaginationEllipsis key="ellipsis-2" />);
      }

      // 마지막 페이지
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink
            onClick={() => handlePageChange(totalPages)}
            isActive={currentPage === totalPages}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  useLoadingIndicator(isUpdating || isLoading || isDeleting);

  return (
    <PageContainer>
      <Header
        title="단어 관리"
        description={category ? `${(category.last_page - 1) * category.words_per_page + category.last_number}개의 단어 · ${totalPages}페이지` : ""}
        right={
          <Button
            onClick={() =>
              navigate({ to: `/vocabulary/categories/${categoryId}/words/upload` })
            }
          >
            <Plus /> 단어 등록
          </Button>
        }
      />

      {!words || words.length === 0 ? (
        <div className="text-center text-gray-500 py-16 border rounded-lg">
          단어가 없습니다. 새 단어를 추가해주세요.
        </div>
      ) : (
        <div className="space-y-6">
          {/* 현재 페이지 정보 및 액션 버튼 */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-semibold">
                Page {currentPage}
              </h2>
              <p className="text-sm text-gray-600">
                {currentPageWords.length}개의 단어
              </p>
            </div>
            <div className="flex gap-2">
              {isEditMode ? (
                <>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="sm:h-12 sm:w-auto sm:px-3"
                  >
                    <X /> <span className="hidden sm:inline">취소</span>
                  </Button>
                  <Button
                    size="icon"
                    variant="destructive"
                    className="sm:h-12 sm:w-auto sm:px-3"
                    onClick={handleDeletePage}
                    disabled={totalPages === 0}
                  >
                    <Trash2 />{" "}
                    <span className="hidden sm:inline">페이지 삭제</span>
                  </Button>
                  <Button
                    size="icon"
                    onClick={handleSave}
                    className="sm:h-12 sm:w-auto sm:px-3"
                  >
                    <Save /> <span className="hidden sm:inline">저장</span>
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => setIsPrintDialogOpen(true)}
                    className="sm:h-12 sm:w-auto sm:px-3"
                  >
                    <Printer /> <span className="hidden sm:inline">인쇄</span>
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleEnterEditMode}
                    className="sm:h-12 sm:w-auto sm:px-3"
                  >
                    <Pencil /> <span className="hidden sm:inline">수정</span>
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* 단어 테이블 */}
          {isEditMode ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">No.</TableHead>
                    <TableHead className="w-1/2">Word</TableHead>
                    <TableHead className="w-1/2">Meaning</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SortableContext
                    items={editedWords.map((w) => w._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {editedWords.map((word) => (
                      <SortableWordRow
                        key={word._id}
                        word={word}
                        onWordChange={handleWordChange}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </DndContext>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">No.</TableHead>
                  <TableHead className="w-1/2">Word</TableHead>
                  <TableHead className="w-1/2">Meaning</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentPageWords
                  .sort((a, b) => a.number - b.number)
                  .map((word) => (
                    <TableRow key={word._id}>
                      <TableCell className="font-medium">
                        {word.number}
                      </TableCell>
                      <TableCell className="font-bold text-lg">
                        {word.word}
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {word.meaning}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}

          {/* 페이지네이션 */}
          {totalPages > 1 && !isEditMode && (
            <Pagination>
              <PaginationContent>
                <PaginationPrevious
                  onClick={() =>
                    currentPage > 1 && handlePageChange(currentPage - 1)
                  }
                  className={
                    currentPage === 1 ? "pointer-events-none opacity-50" : ""
                  }
                />
                {renderPaginationItems()}
                <PaginationNext
                  onClick={() =>
                    currentPage < totalPages &&
                    handlePageChange(currentPage + 1)
                  }
                  className={
                    currentPage === totalPages
                      ? "pointer-events-none opacity-50"
                      : ""
                  }
                />
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}

      <PrintDialog
        open={isPrintDialogOpen}
        onOpenChange={setIsPrintDialogOpen}
        categoryId={categoryId}
        totalPages={totalPages}
        defaultPage={currentPage}
        wordsPerPage={category?.words_per_page ?? 0}
        lastPageWordCount={category?.last_number ?? 0}
      />
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/vocabulary/categories/$id/words/")({
  component: WordsPage,
});
